export interface AppConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'danger';
}

export type AppAlertOptions = Pick<AppConfirmOptions, 'title' | 'message' | 'confirmLabel'>;
export interface AppDialogRequest extends AppConfirmOptions { id: number; kind: 'confirm' | 'alert'; }
interface PendingDialog { request: AppDialogRequest; resolve: (confirmed: boolean) => void; }

let sequence = 0;
let hosts = 0;
let queue: PendingDialog[] = [];
const listeners = new Set<() => void>();
const notify = () => { listeners.forEach((listener) => listener()); };

const enqueue = (kind: AppDialogRequest['kind'], options: AppConfirmOptions): Promise<boolean> => (
  new Promise((resolve) => {
    // Without a mounted host, requests must never wait indefinitely or proceed as approved.
    if (!hosts) {
      resolve(false);

      return;
    }
    queue.push({ request: { ...options, kind, id: ++sequence }, resolve });
    notify();
  })
);

export const confirmAppDialog = (options: AppConfirmOptions): Promise<boolean> => enqueue('confirm', options);
export const alertAppDialog = async (options: AppAlertOptions): Promise<void> => {
  await enqueue('alert', { confirmLabel: 'OK', ...options });
};

const cancelPending = () => {
  const pending = queue;

  queue = [];
  pending.forEach(({ resolve }) => resolve(false));
  notify();
};

/** Internal host boundary: one visible dialog, with stale responses unable to answer the next request. */
export const appDialogStore = {
  getSnapshot: (): AppDialogRequest | null => queue[0]?.request ?? null,
  subscribe: (listener: () => void) => {
    listeners.add(listener);

    return () => { listeners.delete(listener); };
  },
  answer: (id: number, confirmed: boolean) => {
    if (queue[0]?.request.id !== id) return;
    const pending = queue.shift();

    pending?.resolve(confirmed);
    notify();
  },
  cancelPending,
  attach: () => {
    hosts += 1;

    return () => { hosts -= 1; if (!hosts) cancelPending(); };
  },
};
